import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { authApi, ApiError } from '../services/api';
import { clearSsoSession, consumeSsoSession, startSsoLogin } from '../utils/ssoAuth';
import { useAuth } from '../hooks/useAuth';

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const session = consumeSsoSession();

    if (!code || !state || !session) {
      setError('Invalid or expired sign-in session. Please try again.');
      return;
    }

    if (state !== session.state) {
      clearSsoSession();
      setError('Sign-in state mismatch. Please try again.');
      return;
    }

    authApi
      .ssoCallback(code, session.codeVerifier, state)
      .then(async () => {
        clearSsoSession();
        await refreshUser();
        navigate(session.returnTo, { replace: true });
      })
      .catch((err) => {
        clearSsoSession();
        setError(err instanceof ApiError ? err.message : 'Sign-in failed');
      });
  }, [searchParams, navigate, refreshUser]);

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="bg-background-secondary border border-border rounded-xl p-8 max-w-md w-full text-center">
          <AlertCircle className="w-10 h-10 text-error mx-auto mb-4" />
          <h1 className="text-lg font-semibold text-foreground mb-2">Sign-in Failed</h1>
          <p className="text-sm text-foreground-secondary mb-6">{error}</p>
          <button
            onClick={() => startSsoLogin('/')}
            className="btn-primary px-6 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
        <p className="text-foreground-secondary text-sm">Completing sign-in...</p>
      </div>
    </div>
  );
}
